import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {ToastrService} from 'ngx-toastr';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {IAuthenticationState, LogoutUser} from '@app/stores';
import {AuthenticationService} from '@app/services/authentication.service';
import {LoaderService} from '@app/services/loader.service';
import {SessionTimeoutReason, SessionTimeoutService} from '@app/services/session-timeout.service';
import { TranslateModule } from '@ngx-translate/core';

export type TokenRefreshResult = 'refreshed' | 'logout';

/**
 * Body of the session-expiry modal opened by AppComponent through SbModalService.
 * It only renders the live countdown that SessionTimeoutService already produces from the
 * X-Session-* response headers; AppComponent owns the modal ref and closes it when the
 * warning clears.
 */
@Component({
  selector: 'app-token-refresh',
  templateUrl: './token-refresh.component.html',
  styleUrls: ['./token-refresh.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule]
})
export class TokenRefreshComponent implements OnInit, OnDestroy {
  private readonly sessionTimeoutService = inject(SessionTimeoutService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly authenticationStore = inject<Store<IAuthenticationState>>(Store);
  private readonly loaderService = inject(LoaderService);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);

  readonly remainingSeconds = signal(0);
  readonly reason = signal<SessionTimeoutReason>('idle_timeout');
  readonly refreshing = signal(false);

  // Assigned by SbModalHostComponent when opened through SbModalService.
  closeModal: (result?: TokenRefreshResult) => void = () => undefined;
  dismissModal: (reason?: string) => void = () => undefined;

  private readonly unsubscriber = new Subject<void>();

  ngOnInit(): void {
    this.sessionTimeoutService.warning$.pipe(takeUntil(this.unsubscriber))
      .subscribe((warning) => {
        if (!warning) {
          return;
        }
        this.remainingSeconds.set(warning.remainingSeconds);
        this.reason.set(warning.reason);
      });
  }

  /**
   * Only an idle timeout can be put off. A refresh never moves a session's absolute limit, so
   * offering "Stay signed in" then would only re-open this countdown straight away - which a
   * sign-in without "Remember me" reaches every day, after 12 hours.
   */
  get canExtend(): boolean {
    return this.reason() !== 'absolute_timeout';
  }

  /** Keep working until the session ends; the modal stays closed for the rest of this warning. */
  continueWorking(): void {
    this.dismissModal('acknowledged');
  }

  staySignedIn(): void {
    if (this.refreshing()) {
      return;
    }
    this.refreshing.set(true);
    this.authenticationService.refreshSession().pipe(takeUntil(this.unsubscriber))
      .subscribe({
        next: (response: any) => {
          // Same bookkeeping the interceptor does after a silent refresh, store included: the
          // editors and iframes read the token from the store, and would keep the old one.
          this.authenticationService.applySessionResponse(response);
          this.refreshing.set(false);
          this.closeModal('refreshed');
        },
        error: (error) => {
          this.refreshing.set(false);
          this.toastr.error(this.loaderService.getErrorMessage(error));
        }
      });
  }

  signOut(): void {
    // Mirrors the header's "Log out" link (AppComponent.logout()).
    this.loaderService.unsubscribe();
    this.authenticationStore.dispatch(LogoutUser());
    this.router.navigate(['/login']);
    this.closeModal('logout');
  }

  ngOnDestroy(): void {
    this.unsubscriber.next();
    this.unsubscriber.complete();
  }
}
